'use client';

import { useEffect } from 'react';
import { createClient } from '@/lib/supabase/browser';

/**
 * Tracks user activity (mouse, keyboard, scroll, touch) and refreshes the auth session
 * when the user has been active within the last 5 minutes, with a 25-minute delay
 * after last activity before attempting refresh. Helps keep the session alive during
 * active use without hammering the auth server.
 */
export function useActivityTracker() {
  const supabase = createClient();

  useEffect(() => {
    let lastActivity = Date.now();
    let activityTimeout: ReturnType<typeof setTimeout>;

    const refreshSessionIfNeeded = async () => {
      const timeSinceLastActivity = Date.now() - lastActivity;

      // If user has been active in last 5 minutes, refresh session
      if (timeSinceLastActivity < 300000) {
        // 5 minutes
        console.log('[Activity Tracker] User active, refreshing session');
        await supabase.auth.refreshSession();
      }
    };

    const trackActivity = () => {
      lastActivity = Date.now();

      // Clear existing timeout
      clearTimeout(activityTimeout);

      // Set new timeout to refresh session in 25 minutes if still active
      activityTimeout = setTimeout(refreshSessionIfNeeded, 1500000); // 25 minutes
    };

    // Track various user activities
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach((event) => {
      window.addEventListener(event, trackActivity);
    });

    // Initial track
    trackActivity();

    // Cleanup
    return () => {
      clearTimeout(activityTimeout);
      events.forEach((event) => {
        window.removeEventListener(event, trackActivity);
      });
    };
  }, [supabase]);
}
